import { pageTitle } from '@palaccount/config';
import { ArrowRight, Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pager } from '@/components/pager';
import { usePagedState, usePageRange } from '@/lib/paging';

import { InventoryTabs } from './inventory-tabs';
import { TransferModeChip, TransferStatusChip } from './stock-chips';
import { useStockTransfers } from './stock-api';
import { trimZeros } from './conversion';

/** INV-07 §4 — the transfer register: where stock is going and how far it got. */
export function TransfersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** NFR-42 — the page rides in the URL. */
  const paging = usePagedState();
  const { data, isPending, isError } = useStockTransfers(paging.page, paging.pageSize);
  const transfers = data?.transfers;
  const range = usePageRange(data, paging.pageSize);

  useEffect(() => {
    document.title = pageTitle(t('trf.title'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('trf.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createTransfers">
          <Button asChild>
            <Link to="/inventory/transfers/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : (transfers ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('trf.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('trf.colCode')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('trf.colRoute')}</TableHead>
                  <TableHead>{t('trf.colMode')}</TableHead>
                  <TableHead className="text-end">{t('trf.colQty')}</TableHead>
                  <TableHead>{t('trf.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(transfers ?? []).map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer odd:bg-muted/30"
                    onClick={() => void navigate(`/inventory/transfers/${row.id}`)}
                  >
                    <TableCell>
                      <Ltr value={row.code} className="font-mono text-sm" />
                    </TableCell>
                    <TableCell>
                      <Ltr value={row.transferDate} className="font-mono text-sm" />
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="flex items-center gap-1.5">
                        {row.fromWarehouseName}
                        <ArrowRight className="size-3.5 shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden />
                        {row.toWarehouseName}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TransferModeChip mode={row.mode} />
                    </TableCell>
                    <TableCell className="text-end">
                      {/*
                        * NO UNIT, and `N-14` is why rather than an oversight.
                        * `totalQty` is Σ over ONE transfer's lines, and one
                        * transfer may move 40.5 kg of sheet and 12 boxes of
                        * screws — "adding 3 kg to 4 boxes is meaningless".
                        * There is no unit that would be true of the column and
                        * no unit's decimals to round it to, so it stays the
                        * plain count of units the register has always meant.
                        * The register's own screen (INV-07) prints the unit on
                        * every LINE, which is where `N-4` puts it.
                        */}
                      <Ltr value={trimZeros(row.totalQty)} className="font-mono" />
                    </TableCell>
                    <TableCell>
                      <TransferStatusChip status={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('trf.title')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
